/**
 * `tags` — coloured chips over the **account-wide** vocabulary (`[09§A.7 tags]`: *"tags are
 * account-level"*), with a create row, exactly as the README describes: *"A combobox over the
 * account-wide tag vocabulary, with a `create` row for a new tag."*
 *
 * The names are not in the cell — a tag id resolves through `context.tags` — so a caller that
 * has not loaded the vocabulary sees `#id` chips rather than a wrong name.
 */
import { getColumnType, type TagsSettings, type TagsValue } from '@elizmonday/shared';
import { useState } from 'react';
import { CellPopover, PickerSearch, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import type { CellRenderContext, TagRef } from '../types';
import { TagsSettingsDialog } from './SettingsDialog';

const shared = getColumnType('tags');

export const TAGS_PICKER_WIDTH = 280;

function labelOf(id: number, context: CellRenderContext): string {
  return context.tags?.find((tag) => Number(tag.id) === id)?.name ?? `#${String(id)}`;
}

function colourOf(id: number, context: CellRenderContext): string | undefined {
  return context.tags?.find((tag) => Number(tag.id) === id)?.color ?? undefined;
}

export const tagsColumnUi = defineColumnUi<TagsValue, TagsSettings>({
  type: 'tags',
  icon: 'columnDropdown',
  gallery: { group: 'essentials', keywords: ['tags', 'labels', 'keywords', 'topics'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, context }) => {
    const ids = value?.tag_ids ?? [];
    if (ids.length === 0) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-chips" data-align="left">
        {ids.slice(0, 3).map((id) => (
          <span
            key={id}
            className="ez-chip ez-chip--tag"
            style={colourOf(id, context) ? { backgroundColor: colourOf(id, context) } : undefined}
          >
            {labelOf(id, context)}
          </span>
        ))}
        {ids.length > 3 ? <span className="ez-chip ez-chip--more">{`+${ids.length - 3}`}</span> : null}
      </span>
    );
  },

  Editor: ({ value, initialInput, context, onCommit, onCancel }) => {
    const [query, setQuery] = useState(initialInput ?? '');
    /** **P-282**: the picked set follows the store while untouched; an untouched dismiss
     * writes nothing (`useEditorDraft`). */
    const editor = useEditorDraft<number[]>(value?.tag_ids ? [...value.tag_ids] : []);
    const picked = editor.draft;
    /*
     * P-122: a tag minted while this picker is open is not in `context.tags` yet — the board
     * re-reads its vocabulary after the commit — so it is held here for as long as the popover
     * lives, and the chip shows its name straight away rather than `#id`.
     */
    const [minted, setMinted] = useState<readonly TagRef[]>([]);
    const [creating, setCreating] = useState(false);
    const vocabulary: readonly TagRef[] = [...(context.tags ?? []), ...minted];
    const needle = query.trim().toLowerCase();
    const options = vocabulary.filter((tag) => needle === '' || tag.name.toLowerCase().includes(needle));
    const exact = vocabulary.some((tag) => tag.name.toLowerCase() === needle);
    /** The label over the merged vocabulary, so a just-minted tag reads as its name at once. */
    const nameOf = (id: number): string =>
      vocabulary.find((tag) => Number(tag.id) === id)?.name ?? `#${String(id)}`;
    const toggle = (id: number): void =>
      editor.update((current) =>
        current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
      );
    const create = context.createTag;
    const createTyped = async (): Promise<void> => {
      const name = query.trim();
      if (create === undefined || name === '' || creating) return;
      setCreating(true);
      try {
        const tag = await create(name);
        setMinted((current) => [...current, tag]);
        toggle(Number(tag.id));
        setQuery('');
      } finally {
        setCreating(false);
      }
    };
    return (
      <CellPopover
        label="Pick tags"
        width={TAGS_PICKER_WIDTH}
        onDismiss={() =>
          editor.dismiss(() => onCommit(picked.length === 0 ? null : { tag_ids: picked }), onCancel)
        }
        onCancel={onCancel}
      >
        <div className="ez-chosen-chips" data-testid="tags-chosen">
          {picked.map((id) => (
            <span key={id} className="ez-chip ez-chip--tag" data-testid={`tag-chip-${String(id)}`}>
              {nameOf(id)}
              <button
                type="button"
                className="ez-chip__remove"
                data-testid={`chip-remove-${String(id)}`}
                aria-label={`Remove ${nameOf(id)}`}
                onClick={() => toggle(id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <PickerSearch value={query} onChange={setQuery} placeholder="Add or create a tag" />
        <ul className="ez-option-list" role="listbox" aria-label="Tags" aria-multiselectable="true">
          {options.map((tag) => (
            <li key={tag.id}>
              <button
                type="button"
                role="option"
                aria-selected={picked.includes(Number(tag.id))}
                className="ez-option-list__option"
                data-testid={`tag-option-${String(tag.id)}`}
                onClick={() => toggle(Number(tag.id))}
              >
                <span className="ez-option-list__check">{picked.includes(Number(tag.id)) ? '✓' : ''}</span>
                {tag.name}
              </button>
            </li>
          ))}
          {/*
            * monday's create row. Creating an account tag is `create_or_get_tag` — a *board
            * structure* call, not a cell write (q340) — so the picker never invents an id: it
            * asks the board screen through `context.createTag`, which is `tags.createOrGet`.
            * Without that hook the row is still there and still disabled, as it always was.
            */}
          {needle !== '' && !exact ? (
            <li>
              <button
                type="button"
                className="ez-option-list__option"
                data-testid="tag-create"
                disabled={create === undefined || creating}
                onClick={() => void createTyped()}
              >
                {`Create "${query.trim()}"`}
              </button>
            </li>
          ) : null}
          {options.length === 0 && needle === '' ? (
            <li className="ez-option-list__empty">No tags in this account yet</li>
          ) : null}
        </ul>
      </CellPopover>
    );
  },

  /** Names in, ids out — a paste can only reference tags the account already has. */
  SettingsDialog: TagsSettingsDialog,

  fromClipboard(text, settings, context) {
    const names = text
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
    if (names.length === 0) return null;
    const ids = names.map((name) => {
      const tag = (context.tags ?? []).find((entry) => entry.name.toLowerCase() === name.toLowerCase());
      if (tag === undefined) throw new Error(`no tag named "${name}"`);
      return Number(tag.id);
    });
    return shared.parseValue({ tag_ids: ids }, settings) as TagsValue | null;
  },
  /**
   * The names, exactly as `fromClipboard` above reads them back — the same pair P-126 fixed for
   * `people`. The names are not in the cell, so `toText` is given the context's resolver;
   * without one it would answer `""` and a copied cell could never be pasted.
   */
  toClipboard(value, settings, context) {
    const byId = new Map((context.tags ?? []).map((tag) => [Number(tag.id), tag.name]));
    return shared.toText(value, settings, { resolveTagName: (id) => byId.get(Number(id)) }) ?? '';
  },
  bucketsOf(value, _settings, context) {
    const ids = (value as TagsValue | null)?.tag_ids ?? [];
    return ids.map((id) => ({ key: id, label: labelOf(id, context) }));
  },
});
